/* Smokey Hill Designs: Wish lists ♡
 * - Heart button on product pages (blocks/shd-wishlist-button.liquid)
 * - Login prompt for logged-out shoppers (resumes the save after login via sessionStorage)
 * - "Saved to ♡ <list>" toast with a list picker
 * - My Wish List page (sections/shd-wish-list.liquid)
 * Writes go through the app proxy /apps/wish-list/api (signed by Shopify; no tokens in the theme).
 * Preview-only demo mode: add ?wl_demo=1 on an unpublished theme preview. Uses localStorage.
 */
(() => {
  'use strict';
  if (window.SHDWishlist) return;

  const cfg = Object.assign(
    { loggedIn: false, data: null, proxy: '/apps/wish-list/api', loginPath: '/customer_authentication/login', pageUrl: '/pages/wish-list', currency: 'USD', locale: 'en' },
    window.SHDWishlistConfig || {}
  );
  const root = (window.Shopify && Shopify.routes && Shopify.routes.root) || '/';
  const DEFAULT_ID = 'favorites';
  const DEFAULT_NAME = 'My Favorites';
  const LIMITS = { maxLists: 25, maxItemsPerList: 500, maxNameLength: 40 };
  const PENDING_KEY = 'shd_wl_pending';
  const DEMO_KEY = 'shd_wl_demo';
  const DEMO_DATA_KEY = 'shd_wl_demo_data';
  const reduceMotion = window.matchMedia && matchMedia('(prefers-reduced-motion: reduce)').matches;

  // ---------- demo mode (preview themes only) ----------
  const isPreviewTheme = !(window.Shopify && Shopify.theme && Shopify.theme.role === 'main');
  const qs = new URLSearchParams(location.search);
  try {
    if (qs.get('wl_demo') === '1' && isPreviewTheme) sessionStorage.setItem(DEMO_KEY, '1');
    if (qs.get('wl_demo') === '0') sessionStorage.removeItem(DEMO_KEY);
  } catch (e) { /* storage blocked */ }
  const DEMO = isPreviewTheme && safeGet(sessionStorage, DEMO_KEY) === '1';

  // ---------- shared (read-only) wish list links: ?shared=handle~variantId,handle&name=List+name ----------
  const SHARE_MAX_ITEMS = 50;
  function parseShared(raw) {
    if (!raw) return [];
    const seen = new Set();
    const out = [];
    raw.split(',').forEach((part) => {
      const bits = part.split('~');
      const handle = (bits[0] || '').trim();
      if (!handle || handle.length > 255 || /[\/\\?#\s]/.test(handle) || seen.has(handle)) return;
      seen.add(handle);
      out.push({ handle, variant_id: /^\d{1,20}$/.test(bits[1] || '') ? Number(bits[1]) : null });
    });
    return out.slice(0, SHARE_MAX_ITEMS);
  }
  const SHARED = qs.has('shared') ? { items: parseShared(qs.get('shared')), name: (qs.get('name') || '').replace(/[\u0000-\u001f\u007f<>]/g, '').replace(/\s+/g, ' ').trim().slice(0, 40) } : null;
  if (SHARED) {
    // shared links are private-ish snapshots: keep them out of search results
    let robots = document.head && document.head.querySelector('meta[name="robots"]');
    if (!robots && document.head) { robots = document.createElement('meta'); robots.name = 'robots'; document.head.append(robots); }
    if (robots) robots.content = 'noindex,nofollow';
  }
  const loggedIn = DEMO || !!cfg.loggedIn;

  function safeGet(store, k) { try { return store.getItem(k); } catch (e) { return null; } }
  function safeSet(store, k, v) { try { store.setItem(k, v); } catch (e) { /* ignore */ } }
  function safeDel(store, k) { try { store.removeItem(k); } catch (e) { /* ignore */ } }

  // ---------- data logic (mirrors worker/src/lists.js) ----------
  class WLError extends Error { constructor(code, status) { super(code); this.code = code; this.status = status || 400; } }
  const emptyData = () => ({ lists: [{ id: DEFAULT_ID, name: DEFAULT_NAME, items: [] }] });
  const posInt = (v) => { const n = typeof v === 'string' && /^\d+$/.test(v) ? Number(v) : v; return Number.isSafeInteger(n) && n > 0; };
  function cleanName(n) { return typeof n === 'string' ? n.replace(/[\u0000-\u001f\u007f<>]/g, '').replace(/\s+/g, ' ').trim().slice(0, LIMITS.maxNameLength) : ''; }
  function normalize(raw) {
    let d = raw;
    if (typeof d === 'string') { try { d = JSON.parse(d); } catch (e) { d = null; } }
    if (!d || !Array.isArray(d.lists)) return emptyData();
    const seen = new Set();
    const lists = [];
    d.lists.forEach((l) => {
      if (!l || typeof l.id !== 'string' || seen.has(l.id)) return;
      seen.add(l.id);
      lists.push({
        id: l.id,
        name: cleanName(l.name) || DEFAULT_NAME,
        items: (Array.isArray(l.items) ? l.items : []).filter((i) => i && posInt(i.product_id)).map((i) => ({
          product_id: Number(i.product_id), variant_id: posInt(i.variant_id) ? Number(i.variant_id) : null,
          handle: typeof i.handle === 'string' ? i.handle : '', added_at: i.added_at || null,
        })),
      });
    });
    return lists.length ? { lists } : emptyData();
  }
  const same = (i, p, v) => i.product_id === p && (v == null || i.variant_id === v);
  function findList(d, id) { const l = d.lists.find((x) => x.id === id); if (!l) throw new WLError('list_not_found', 404); return l; }
  function newList(d, name) {
    const n = cleanName(name);
    if (!n) throw new WLError('invalid_name');
    if (d.lists.length >= LIMITS.maxLists) throw new WLError('too_many_lists', 422);
    if (d.lists.some((l) => l.name.toLowerCase() === n.toLowerCase())) throw new WLError('duplicate_name', 409);
    const l = { id: 'l_' + Math.random().toString(36).slice(2, 10), name: n, items: [] };
    d.lists.push(l);
    return l;
  }
  function insert(l, it) {
    const ex = l.items.find((i) => i.product_id === it.product_id && i.variant_id === it.variant_id);
    if (ex) return true;
    if (l.items.length >= LIMITS.maxItemsPerList) throw new WLError('list_full', 422);
    l.items.unshift(it);
    return false;
  }
  function applyAction(input, a) {
    const d = JSON.parse(JSON.stringify(normalize(input)));
    let result = {};
    const pid = a.product_id != null ? Number(a.product_id) : null;
    const vid = a.variant_id ? Number(a.variant_id) : null;
    switch (a.action) {
      case 'get': break;
      case 'add': {
        if (!posInt(pid)) throw new WLError('invalid_product_id');
        const l = a.list_name !== undefined && !a.list_id ? newList(d, a.list_name) : a.list_id ? findList(d, a.list_id) : d.lists[0];
        const already = insert(l, { product_id: pid, variant_id: vid, handle: a.handle || '', added_at: new Date().toISOString() });
        result = { list_id: l.id, list_name: l.name, already };
        break;
      }
      case 'remove': {
        let removed = 0;
        (a.list_id ? [findList(d, a.list_id)] : d.lists).forEach((l) => { const b = l.items.length; l.items = l.items.filter((i) => !same(i, pid, vid)); removed += b - l.items.length; });
        result = { removed };
        break;
      }
      case 'move': {
        const from = a.from_list_id ? findList(d, a.from_list_id) : d.lists.find((l) => l.items.some((i) => same(i, pid, vid)));
        if (!from) throw new WLError('item_not_found', 404);
        const to = a.to_list_id ? findList(d, a.to_list_id) : newList(d, a.to_list_name);
        const moving = from.items.filter((i) => same(i, pid, vid));
        if (!moving.length) throw new WLError('item_not_found', 404);
        if (from.id !== to.id) { from.items = from.items.filter((i) => !same(i, pid, vid)); moving.reverse().forEach((it) => insert(to, it)); }
        result = { from_list_id: from.id, to_list_id: to.id, list_name: to.name };
        break;
      }
      case 'create_list': { const l = newList(d, a.name); result = { list_id: l.id, list_name: l.name }; break; }
      case 'rename_list': {
        const l = findList(d, a.list_id); const n = cleanName(a.name);
        if (!n) throw new WLError('invalid_name');
        if (d.lists.some((x) => x.id !== l.id && x.name.toLowerCase() === n.toLowerCase())) throw new WLError('duplicate_name', 409);
        l.name = n; result = { list_id: l.id, list_name: n }; break;
      }
      case 'delete_list': {
        const l = findList(d, a.list_id);
        if (d.lists.length <= 1) throw new WLError('last_list', 422);
        d.lists = d.lists.filter((x) => x.id !== l.id); result = { deleted: l.id }; break;
      }
      default: throw new WLError('unknown_action');
    }
    return { data: d, result };
  }

  // ---------- state ----------
  const state = {
    data: DEMO ? normalize(safeGet(localStorage, DEMO_DATA_KEY)) : normalize(cfg.data),
    busy: new Set(),
  };
  function setLists(lists) {
    state.data = normalize({ lists });
    if (DEMO) safeSet(localStorage, DEMO_DATA_KEY, JSON.stringify(state.data));
    syncButtons();
    document.dispatchEvent(new CustomEvent('shd-wishlist:change', { detail: state.data }));
  }
  const listsWith = (pid) => state.data.lists.filter((l) => l.items.some((i) => i.product_id === pid));
  const isSaved = (pid) => listsWith(pid).length > 0;

  async function api(action) {
    if (DEMO) {
      await new Promise((r) => setTimeout(r, 120));
      const { data, result } = applyAction(state.data, action);
      return { ok: true, lists: data.lists, result };
    }
    let res;
    try {
      res = await fetch(cfg.proxy, action.action === 'get'
        ? { credentials: 'same-origin', headers: { Accept: 'application/json' } }
        : { method: 'POST', credentials: 'same-origin', headers: { 'Content-Type': 'application/json', Accept: 'application/json' }, body: JSON.stringify(action) });
    } catch (e) { throw new WLError('unavailable', 0); }
    const ct = res.headers.get('content-type') || '';
    if (!ct.includes('json')) throw new WLError('unavailable', res.status); // proxy not live yet -> Shopify 404 page
    let j = {};
    try { j = await res.json(); } catch (e) { throw new WLError('unavailable', res.status); }
    if (res.status === 401 && j.error === 'login_required') throw new WLError('login_required', 401);
    if (!res.ok || !j.ok) throw new WLError(j.error || 'error', res.status);
    return j;
  }

  async function run(action) {
    const j = await api(action);
    if (j.lists) setLists(j.lists);
    return j.result || {};
  }

  const ERROR_TEXT = {
    duplicate_name: 'You already have a list with that name.',
    invalid_name: 'Please give your list a name.',
    too_many_lists: 'You’ve reached the maximum number of lists.',
    list_full: 'That list is full. Try another one!',
    last_list: 'You need at least one list.',
    too_large: 'Your wish lists are full. Remove a few items first.',
  };
  function handleError(e) {
    if (e && e.code === 'login_required') { showLogin(); return; }
    if (e && e.code === 'unavailable') { toast({ title: 'Wish lists are almost ready!', text: 'We’re putting the finishing touches on them. Check back soon ♡', icon: 'sparkle' }); return; }
    toast({ title: 'Oops!', text: (e && ERROR_TEXT[e.code]) || 'Something went wrong. Please try again.', icon: 'oops' });
  }

  // ---------- tiny DOM helpers (text only; never innerHTML user data) ----------
  function el(tag, attrs, ...kids) {
    const n = document.createElement(tag);
    if (attrs) Object.entries(attrs).forEach(([k, v]) => {
      if (v == null || v === false) return;
      if (k === 'class') n.className = v;
      else if (k === 'text') n.textContent = v;
      else if (k.startsWith('on')) n.addEventListener(k.slice(2), v);
      else n.setAttribute(k, v === true ? '' : v);
    });
    kids.flat().forEach((c) => { if (c != null && c !== false) n.append(c.nodeType ? c : document.createTextNode(String(c))); });
    return n;
  }
  const SVG = {
    heart: '<svg viewBox="0 0 24 24" aria-hidden="true" focusable="false"><path d="M12 20.6s-7.1-4.4-9.4-8.6C.9 8.6 2.6 4.6 6.3 4.1c2.2-.3 4 .8 5.7 2.9 1.7-2.1 3.5-3.2 5.7-2.9 3.7.5 5.4 4.5 3.7 7.9-2.3 4.2-9.4 8.6-9.4 8.6z"/></svg>',
    sparkle: '<svg viewBox="0 0 24 24" aria-hidden="true" focusable="false"><path d="M12 2l1.9 6.1L20 10l-6.1 1.9L12 18l-1.9-6.1L4 10l6.1-1.9z"/><path d="M19 15l.8 2.2L22 18l-2.2.8L19 21l-.8-2.2L16 18l2.2-.8z"/></svg>',
    oops: '<svg viewBox="0 0 24 24" aria-hidden="true" focusable="false"><circle cx="12" cy="12" r="10"/><path d="M12 7v6M12 16.5v.5" stroke="#fff" stroke-width="2" stroke-linecap="round"/></svg>',
    cart: '<svg viewBox="0 0 24 24" aria-hidden="true" focusable="false"><path d="M3 4h2l2.4 10.2a2 2 0 0 0 2 1.6h7.7a2 2 0 0 0 2-1.5L21 8H6.2" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"/><circle cx="10" cy="20" r="1.5"/><circle cx="17" cy="20" r="1.5"/></svg>',
    close: '<svg viewBox="0 0 24 24" aria-hidden="true" focusable="false"><path d="M6 6l12 12M18 6L6 18" stroke="currentColor" stroke-width="2.2" stroke-linecap="round"/></svg>',
    pencil: '<svg viewBox="0 0 24 24" aria-hidden="true" focusable="false"><path d="M4 20h4L19 9l-4-4L4 16v4z" fill="none" stroke="currentColor" stroke-width="2" stroke-linejoin="round"/></svg>',
    trash: '<svg viewBox="0 0 24 24" aria-hidden="true" focusable="false"><path d="M5 7h14M10 7V4h4v3M7 7l1 13h8l1-13" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"/></svg>',
    plus: '<svg viewBox="0 0 24 24" aria-hidden="true" focusable="false"><path d="M12 5v14M5 12h14" stroke="currentColor" stroke-width="2.4" stroke-linecap="round"/></svg>',
    share: '<svg viewBox="0 0 24 24" aria-hidden="true" focusable="false"><path d="M12 15V4M8 8l4-4 4 4M5 13v6a1 1 0 0 0 1 1h12a1 1 0 0 0 1-1v-6" fill="none" stroke="currentColor" stroke-width="2.2" stroke-linecap="round" stroke-linejoin="round"/></svg>',
    mail: '<svg viewBox="0 0 24 24" aria-hidden="true" focusable="false"><rect x="3" y="5" width="18" height="14" rx="2.5" fill="none" stroke="currentColor" stroke-width="2"/><path d="M4 7l8 6 8-6" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"/></svg>',
    copy: '<svg viewBox="0 0 24 24" aria-hidden="true" focusable="false"><rect x="9" y="9" width="11" height="11" rx="2.5" fill="none" stroke="currentColor" stroke-width="2"/><path d="M5 15V6a2 2 0 0 1 2-2h8" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round"/></svg>',
    check: '<svg viewBox="0 0 24 24" aria-hidden="true" focusable="false"><path d="M5 12.5l4.5 4.5L19 7.5" fill="none" stroke="currentColor" stroke-width="2.6" stroke-linecap="round" stroke-linejoin="round"/></svg>',
  };
  function icon(name, cls) { const s = el('span', { class: 'shd-wl-icon ' + (cls || '') }); s.innerHTML = SVG[name]; return s; }

  // ---------- toast ----------
  let toastEl, toastTimer;
  function toast({ title, text, actions = [], icon: ic = 'heart', timeout = 5500 }) {
    if (!toastEl) {
      toastEl = el('div', { class: 'shd-wl-toast', role: 'status', 'aria-live': 'polite' });
      toastEl.addEventListener('mouseenter', () => clearTimeout(toastTimer));
      toastEl.addEventListener('focusin', () => clearTimeout(toastTimer));
      toastEl.addEventListener('mouseleave', () => armToast(3000));
      document.body.append(toastEl);
    }
    toastEl.replaceChildren(
      icon(ic, 'shd-wl-toast__icon shd-wl-toast__icon--' + ic),
      el('div', { class: 'shd-wl-toast__body' },
        el('p', { class: 'shd-wl-toast__title', text: title }),
        text ? el('p', { class: 'shd-wl-toast__text', text }) : null,
        actions.length ? el('div', { class: 'shd-wl-toast__actions' }, actions.map((a) => a.href
          ? el('a', { class: 'shd-wl-link', href: a.href, text: a.label })
          : el('button', { type: 'button', class: 'shd-wl-link', text: a.label, onclick: () => { hideToast(); a.onClick(); } }))) : null),
      el('button', { type: 'button', class: 'shd-wl-toast__close', 'aria-label': 'Close', onclick: hideToast }, icon('close'))
    );
    toastEl.classList.remove('is-visible');
    void toastEl.offsetWidth;
    toastEl.classList.add('is-visible');
    armToast(timeout);
  }
  function armToast(ms) { clearTimeout(toastTimer); toastTimer = setTimeout(hideToast, ms); }
  function hideToast() { if (toastEl) toastEl.classList.remove('is-visible'); }

  // ---------- dialogs ----------
  function openDialog(cls, build, { onClose } = {}) {
    const d = el('dialog', { class: 'shd-wl-dialog ' + cls });
    const close = () => { d.classList.add('is-closing'); setTimeout(() => { if (d.open) d.close(); d.remove(); }, reduceMotion ? 0 : 160); };
    d.append(el('button', { type: 'button', class: 'shd-wl-dialog__x', 'aria-label': 'Close', onclick: close }, icon('close')));
    d.append(build(close));
    d.addEventListener('click', (e) => { if (e.target === d) close(); });
    d.addEventListener('cancel', (e) => { e.preventDefault(); close(); });
    d.addEventListener('close', () => onClose && onClose());
    document.body.append(d);
    if (d.showModal) d.showModal(); else d.setAttribute('open', '');
    return { dialog: d, close };
  }

  function loginHref(returnTo) {
    return root.replace(/\/$/, '') + cfg.loginPath + '?return_to=' + encodeURIComponent(returnTo || location.pathname + location.search);
  }

  function showLogin(returnTo) {
    openDialog('shd-wl-login', (close) => el('div', { class: 'shd-wl-login__inner' },
      el('div', { class: 'shd-wl-login__art', 'aria-hidden': 'true' }, icon('heart', 'shd-wl-login__h1'), icon('heart', 'shd-wl-login__h2'), icon('heart', 'shd-wl-login__h3')),
      el('h2', { class: 'shd-wl-dialog__title', id: 'shd-wl-login-title', text: 'Log in to save your favorites ♡' }),
      el('p', { class: 'shd-wl-dialog__text', text: 'Sign in (or create a free account) and your wish lists are saved to your account forever, on every device.' }),
      el('a', { class: 'shd-wl-pill shd-wl-pill--coral', href: loginHref(returnTo) }, 'Log in'),
      el('button', { type: 'button', class: 'shd-wl-link shd-wl-login__later', text: 'Maybe later', onclick: () => { safeDel(sessionStorage, PENDING_KEY); close(); } })
    ));
  }

  function showPicker(item, currentListId) {
    openDialog('shd-wl-picker', (close) => {
      const wrap = el('div', { class: 'shd-wl-picker__inner' });
      const title = el('h2', { class: 'shd-wl-dialog__title', text: 'Save to a list' });
      const ul = el('ul', { class: 'shd-wl-picker__lists', role: 'list' });
      const choose = async (target) => {
        if (target === currentListId) { close(); return; }
        try {
          const r = await run({ action: 'move', product_id: item.product_id, variant_id: item.variant_id, from_list_id: currentListId, to_list_id: target });
          close();
          savedToast(item, r.to_list_id, r.list_name, 'Moved to');
        } catch (e) { close(); handleError(e); }
      };
      state.data.lists.forEach((l) => {
        const on = l.id === currentListId;
        ul.append(el('li', null, el('button', { type: 'button', class: 'shd-wl-picker__row' + (on ? ' is-current' : ''), 'aria-pressed': on ? 'true' : 'false', onclick: () => choose(l.id) },
          el('span', { class: 'shd-wl-picker__check' }, on ? icon('check') : null),
          el('span', { class: 'shd-wl-picker__name', text: l.name }),
          el('span', { class: 'shd-wl-picker__count', text: String(l.items.length) }))));
      });
      const input = el('input', { class: 'shd-wl-input', type: 'text', maxlength: String(LIMITS.maxNameLength), placeholder: 'New list name, e.g. Christmas ideas', 'aria-label': 'New list name' });
      const err = el('p', { class: 'shd-wl-form-error', role: 'alert' });
      const form = el('form', { class: 'shd-wl-picker__new', onsubmit: async (e) => {
        e.preventDefault();
        const name = cleanName(input.value);
        if (!name) { err.textContent = ERROR_TEXT.invalid_name; input.focus(); return; }
        try {
          const r = await run({ action: 'move', product_id: item.product_id, variant_id: item.variant_id, from_list_id: currentListId, to_list_name: name });
          close();
          savedToast(item, r.to_list_id, r.list_name, 'Moved to');
        } catch (ex) { err.textContent = ERROR_TEXT[ex.code] || 'Couldn’t create that list.'; if (ex.code === 'unavailable' || ex.code === 'login_required') { close(); handleError(ex); } }
      } }, input, el('button', { type: 'submit', class: 'shd-wl-pill shd-wl-pill--teal shd-wl-pill--sm' }, icon('plus'), 'Create'));
      wrap.append(title, ul, el('p', { class: 'shd-wl-picker__label', text: 'Or start a new list' }), form, err);
      setTimeout(() => { const cur = ul.querySelector('.is-current'); (cur || ul.querySelector('button')).focus(); }, 30);
      return wrap;
    });
  }

  function savedToast(item, listId, listName, verb) {
    toast({
      title: (verb || 'Saved to') + ' ♡ ' + listName,
      actions: [
        { label: 'Change list', onClick: () => showPicker(item, listId) },
        { label: 'View wish list', href: cfg.pageUrl + (DEMO ? '?wl_demo=1' : '') },
      ],
    });
  }

  // ---------- heart buttons ----------
  function currentVariantId(btn) {
    const scope = btn.closest('product-information, .product-information, [data-product-id], main') || document;
    const input = scope.querySelector('form[action*="/cart/add"] [name="id"]') || document.querySelector('form[action*="/cart/add"] [name="id"]');
    const fromUrl = new URLSearchParams(location.search).get('variant');
    const v = (input && input.value) || fromUrl || btn.dataset.variantId;
    return posInt(v) ? Number(v) : null;
  }
  function itemFrom(btn) {
    return { product_id: Number(btn.dataset.productId), variant_id: currentVariantId(btn), handle: btn.dataset.handle || '' };
  }
  function paint(btn, saved, pop) {
    btn.setAttribute('aria-pressed', saved ? 'true' : 'false');
    btn.classList.toggle('is-saved', saved);
    const lbl = btn.querySelector('.shd-wl-btn__label');
    const txt = saved ? (btn.dataset.labelSaved || 'Saved to wish list') : (btn.dataset.labelAdd || 'Add to wish list');
    if (lbl && lbl.textContent !== txt) lbl.textContent = txt;
    const al = saved ? 'Remove from wish list' : 'Add to wish list';
    if (btn.getAttribute('aria-label') !== al) btn.setAttribute('aria-label', al);
    if (pop && !reduceMotion) { btn.classList.remove('is-popping'); void btn.offsetWidth; btn.classList.add('is-popping'); }
  }
  function syncButtons() {
    document.querySelectorAll('.shd-wl-btn[data-product-id]').forEach((b) => paint(b, loggedIn && isSaved(Number(b.dataset.productId)), false));
  }

  async function onHeart(btn) {
    const item = itemFrom(btn);
    if (!posInt(item.product_id) || state.busy.has(item.product_id)) return;
    if (!loggedIn) {
      safeSet(sessionStorage, PENDING_KEY, JSON.stringify(Object.assign({ ts: Date.now() }, item)));
      showLogin();
      return;
    }
    state.busy.add(item.product_id);
    btn.classList.add('is-busy');
    const wasSaved = isSaved(item.product_id);
    paint(btn, !wasSaved, !wasSaved); // optimistic
    try {
      if (wasSaved) {
        await run({ action: 'remove', product_id: item.product_id });
        toast({ title: 'Removed from your wish list', icon: 'heart', actions: [{ label: 'Undo', onClick: () => onHeart(btn) }] });
      } else {
        const r = await run({ action: 'add', product_id: item.product_id, variant_id: item.variant_id, handle: item.handle });
        savedToast(item, r.list_id, r.list_name);
      }
    } catch (e) {
      paint(btn, wasSaved, false);
      handleError(e);
    } finally {
      state.busy.delete(item.product_id);
      btn.classList.remove('is-busy');
    }
  }

  async function resumePending() {
    if (!loggedIn) return;
    const raw = safeGet(sessionStorage, PENDING_KEY);
    if (!raw) return;
    safeDel(sessionStorage, PENDING_KEY);
    let p;
    try { p = JSON.parse(raw); } catch (e) { return; }
    if (!p || !posInt(p.product_id) || Date.now() - (p.ts || 0) > 60 * 60 * 1000) return;
    if (isSaved(p.product_id)) { toast({ title: 'Welcome back! ♡', text: 'That item is already on your wish list.' }); return; }
    const btn = document.querySelector('.shd-wl-btn[data-product-id="' + p.product_id + '"]');
    if (btn) paint(btn, true, true);
    try {
      const r = await run({ action: 'add', product_id: p.product_id, variant_id: p.variant_id, handle: p.handle });
      savedToast(p, r.list_id, r.list_name);
    } catch (e) { if (btn) paint(btn, false, false); handleError(e); }
  }

  document.addEventListener('click', (e) => {
    const btn = e.target.closest && e.target.closest('.shd-wl-btn[data-product-id]');
    if (!btn) return;
    e.preventDefault();
    onHeart(btn);
  });

  // ---------- My Wish List page ----------
  const productCache = new Map();
  function fetchProduct(handle) {
    if (!handle) return Promise.resolve(null);
    if (!productCache.has(handle)) {
      productCache.set(handle, fetch(root + 'products/' + encodeURIComponent(handle) + '.js', { headers: { Accept: 'application/json' } })
        .then((r) => (r.ok ? r.json() : null)).catch(() => null));
    }
    return productCache.get(handle);
  }
  let moneyFmt;
  function money(cents) {
    try { moneyFmt = moneyFmt || new Intl.NumberFormat(cfg.locale || 'en', { style: 'currency', currency: cfg.currency || 'USD' }); return moneyFmt.format(cents / 100); }
    catch (e) { return '$' + (cents / 100).toFixed(2); }
  }
  function sizedImage(src, w) {
    if (!src) return '';
    try { const u = new URL(src, location.href); u.searchParams.set('width', String(w)); return u.toString(); } catch (e) { return src; }
  }

  async function addToCart(variantId, btn) {
    btn.disabled = true;
    const label = btn.querySelector('.shd-wl-pill__text');
    const old = label ? label.textContent : '';
    if (label) label.textContent = 'Adding…';
    try {
      const r = await fetch(root + 'cart/add.js', { method: 'POST', headers: { 'Content-Type': 'application/json', Accept: 'application/json' }, body: JSON.stringify({ items: [{ id: variantId, quantity: 1 }] }) });
      if (!r.ok) { const j = await r.json().catch(() => ({})); throw new Error(j.description || 'Could not add to cart'); }
      const cart = await fetch(root + 'cart.js', { headers: { Accept: 'application/json' } }).then((x) => x.json());
      const ev = new Event('cart:update', { bubbles: true });
      ev.detail = { resource: cart, sourceId: 'shd-wishlist', data: { source: 'shd-wishlist', itemCount: cart.item_count, variantId: String(variantId) } };
      document.dispatchEvent(ev);
      toast({ title: 'Added to your cart', icon: 'cart', actions: [{ label: 'View cart', href: root + 'cart' }] });
    } catch (e) {
      toast({ title: 'Oops!', text: e.message || 'Could not add to cart.', icon: 'oops' });
    } finally {
      btn.disabled = false;
      if (label) label.textContent = old;
    }
  }

  function promptDialog({ title, label, value = '', submit, onSubmit }) {
    openDialog('shd-wl-prompt', (close) => {
      const input = el('input', { class: 'shd-wl-input', type: 'text', value, maxlength: String(LIMITS.maxNameLength), 'aria-label': label, placeholder: label });
      const err = el('p', { class: 'shd-wl-form-error', role: 'alert' });
      const f = el('form', { class: 'shd-wl-prompt__form', onsubmit: async (e) => {
        e.preventDefault();
        const n = cleanName(input.value);
        if (!n) { err.textContent = ERROR_TEXT.invalid_name; return; }
        try { await onSubmit(n); close(); }
        catch (ex) { if (ex.code === 'unavailable' || ex.code === 'login_required') { close(); handleError(ex); } else err.textContent = ERROR_TEXT[ex.code] || 'Something went wrong.'; }
      } }, input, err, el('div', { class: 'shd-wl-dialog__buttons' },
        el('button', { type: 'button', class: 'shd-wl-pill shd-wl-pill--ghost', onclick: close }, 'Cancel'),
        el('button', { type: 'submit', class: 'shd-wl-pill shd-wl-pill--teal' }, submit)));
      setTimeout(() => { input.focus(); input.select(); }, 30);
      return el('div', null, el('h2', { class: 'shd-wl-dialog__title', text: title }), f);
    });
  }
  function confirmDialog({ title, text, confirm, onConfirm }) {
    openDialog('shd-wl-confirm', (close) => el('div', null,
      el('h2', { class: 'shd-wl-dialog__title', text: title }),
      el('p', { class: 'shd-wl-dialog__text', text }),
      el('div', { class: 'shd-wl-dialog__buttons' },
        el('button', { type: 'button', class: 'shd-wl-pill shd-wl-pill--ghost', onclick: close }, 'Keep it'),
        el('button', { type: 'button', class: 'shd-wl-pill shd-wl-pill--coral', onclick: async () => { close(); try { await onConfirm(); } catch (e) { handleError(e); } } }, confirm))));
  }


  // ---------- share a list ----------
  function buildShareUrl(list) {
    const usable = list.items.filter((i) => i.handle);
    const items = usable.slice(0, SHARE_MAX_ITEMS);
    const raw = items.map((i) => i.handle + (i.variant_id ? '~' + i.variant_id : '')).join(',');
    const base = location.origin + (cfg.pageUrl || '/pages/wish-list');
    const url = base + '?shared=' + encodeURIComponent(raw).replace(/%2C/g, ',').replace(/%7E/g, '~') + '&name=' + encodeURIComponent(list.name);
    return { url, shown: items.length, total: usable.length };
  }
  async function copyText(text, input) {
    try { if (navigator.clipboard && window.isSecureContext) { await navigator.clipboard.writeText(text); return true; } } catch (e) { /* fall through */ }
    try {
      if (input) { input.focus(); input.select(); input.setSelectionRange(0, text.length); }
      return document.execCommand('copy');
    } catch (e) { return false; }
  }
  function shareDialog(list, link) {
    openDialog('shd-wl-share-dlg', (close) => {
      const input = el('input', { class: 'shd-wl-input shd-wl-share__url', type: 'text', readonly: true, value: link.url, 'aria-label': 'Wish list link', onfocus: (e) => e.target.select() });
      const copyLabel = el('span', { class: 'shd-wl-pill__text', text: 'Copy link' });
      const copyBtn = el('button', { type: 'button', class: 'shd-wl-pill shd-wl-pill--teal', onclick: async () => {
        const ok = await copyText(link.url, input);
        copyLabel.textContent = ok ? 'Link copied!' : 'Press Ctrl+C to copy';
        if (ok) toast({ title: 'Link copied ♡', text: 'Paste it into a message to share “' + list.name + '”.', icon: 'check' });
        setTimeout(() => { copyLabel.textContent = 'Copy link'; }, 2500);
      } }, icon('copy'), copyLabel);
      const subject = 'My Smokey Hill Designs wish list: ' + list.name;
      const body = 'Take a look at my wish list from Smokey Hill Designs:\n\n' + link.url + '\n';
      const mail = el('a', { class: 'shd-wl-pill shd-wl-pill--ghost', href: 'mailto:?subject=' + encodeURIComponent(subject) + '&body=' + encodeURIComponent(body), onclick: () => setTimeout(close, 400) }, icon('mail'), 'Email it');
      return el('div', null,
        el('h2', { class: 'shd-wl-dialog__title', text: 'Share “' + list.name + '”' }),
        el('p', { class: 'shd-wl-dialog__text', text: 'Anyone with this link can see the items on this list. They can’t change your list.' }),
        input,
        link.total > link.shown ? el('p', { class: 'shd-wl-form-error', text: 'Links hold up to ' + SHARE_MAX_ITEMS + ' items, so the ' + link.shown + ' newest are included.' }) : null,
        el('div', { class: 'shd-wl-dialog__buttons' }, copyBtn, mail));
    });
  }
  async function shareList(list) {
    const link = buildShareUrl(list);
    if (!link.shown) { toast({ title: 'Nothing to share yet', text: 'Save a few items to this list first ♡', icon: 'sparkle' }); return; }
    const touch = window.matchMedia && matchMedia('(pointer: coarse)').matches;
    if (touch && navigator.share) {
      try {
        await navigator.share({ title: 'My wish list: ' + list.name, text: 'Take a look at my wish list from Smokey Hill Designs ♡', url: link.url });
        return;
      } catch (e) {
        if (e && e.name === 'AbortError') return; // shopper closed the share sheet
        /* share unsupported or blocked: fall back to the dialog */
      }
    }
    shareDialog(list, link);
  }

  // ---------- shared (read-only) view ----------
  function saveSharedItem(p, variantId, btn) {
    const item = { product_id: p.id, variant_id: variantId || null, handle: p.handle };
    if (!loggedIn) {
      safeSet(sessionStorage, PENDING_KEY, JSON.stringify(Object.assign({ ts: Date.now() }, item)));
      showLogin(location.pathname + location.search);
      return;
    }
    if (isSaved(p.id)) { toast({ title: 'Already on your wish list ♡', actions: [{ label: 'View wish list', href: cfg.pageUrl }] }); return; }
    btn.disabled = true;
    run({ action: 'add', product_id: item.product_id, variant_id: item.variant_id, handle: item.handle })
      .then((r) => { btn.querySelector('.shd-wl-pill__text').textContent = 'Saved ♡'; savedToast(item, r.list_id, r.list_name); })
      .catch(handleError)
      .finally(() => { btn.disabled = false; });
  }

  function sharedCard(it) {
    const li = el('li', { class: 'shd-wl-card is-loading' });
    const href = root + 'products/' + encodeURIComponent(it.handle) + (it.variant_id ? '?variant=' + it.variant_id : '');
    const media = el('a', { class: 'shd-wl-card__media', href, tabindex: '-1', 'aria-hidden': 'true' });
    const title = el('a', { class: 'shd-wl-card__title', href, text: it.handle.replace(/-/g, ' ') });
    const variant = el('p', { class: 'shd-wl-card__variant' });
    const price = el('p', { class: 'shd-wl-card__price' });
    const atc = el('button', { type: 'button', class: 'shd-wl-pill shd-wl-pill--teal shd-wl-pill--sm shd-wl-card__atc', disabled: true }, icon('cart'), el('span', { class: 'shd-wl-pill__text', text: 'Add to cart' }));
    const save = el('button', { type: 'button', class: 'shd-wl-pill shd-wl-pill--ghost shd-wl-pill--sm shd-wl-card__save', disabled: true }, icon('heart'), el('span', { class: 'shd-wl-pill__text', text: 'Save to my wish list' }));
    const view = el('a', { class: 'shd-wl-link shd-wl-card__view', href, text: 'View product' });
    li.append(media, el('div', { class: 'shd-wl-card__info' }, title, variant, price), el('div', { class: 'shd-wl-card__actions' }, atc, save, view));
    fetchProduct(it.handle).then((p) => {
      li.classList.remove('is-loading');
      if (!p) {
        li.classList.add('is-gone');
        variant.textContent = 'This item is no longer available.';
        atc.querySelector('.shd-wl-pill__text').textContent = 'Unavailable';
        save.hidden = true; view.hidden = true;
        return;
      }
      const v = (p.variants || []).find((x) => x.id === it.variant_id) || (p.variants || []).find((x) => x.available) || (p.variants || [])[0];
      title.textContent = p.title;
      if (v && p.variants.length > 1 && v.title && v.title !== 'Default Title') variant.textContent = v.title;
      const img = (v && v.featured_image && v.featured_image.src) || p.featured_image;
      if (img) media.append(el('img', { src: sizedImage(img, 600), srcset: sizedImage(img, 300) + ' 300w, ' + sizedImage(img, 600) + ' 600w', sizes: '(min-width: 990px) 25vw, 50vw', alt: p.title, loading: 'lazy', width: '600', height: '600' }));
      const cents = v ? v.price : p.price;
      price.replaceChildren(el('span', { text: money(cents) }));
      const compare = v ? v.compare_at_price : p.compare_at_price;
      if (compare && compare > cents) { price.append(el('s', { class: 'shd-wl-card__compare', text: money(compare) })); li.classList.add('is-sale'); }
      if (v && v.available) { atc.disabled = false; atc.addEventListener('click', () => addToCart(v.id, atc)); }
      else { atc.querySelector('.shd-wl-pill__text').textContent = 'Sold out'; li.classList.add('is-soldout'); }
      save.disabled = false;
      if (loggedIn && isSaved(p.id)) save.querySelector('.shd-wl-pill__text').textContent = 'Saved ♡';
      save.addEventListener('click', () => saveSharedItem(p, v && v.id, save));
    });
    return li;
  }

  function initSharedPage(rootEl) {
    const body = rootEl.querySelector('[data-shd-wl-body]') || rootEl;
    const setText = (sel, text) => { const n = rootEl.querySelector(sel); if (n) n.textContent = text; };
    setText('.shd-wl-page__kicker', '♡ Shared with you');
    setText('.shd-wl-page__title', SHARED.name || 'A shared wish list');
    setText('.shd-wl-page__sub', 'Someone thought of you! Add anything to your cart, or save it to your own wish list.');
    body.replaceChildren();
    if (!SHARED.items.length) {
      body.append(el('div', { class: 'shd-wl-empty' },
        el('div', { class: 'shd-wl-empty__art', 'aria-hidden': 'true' }, icon('heart', 'shd-wl-empty__heart'), icon('sparkle', 'shd-wl-empty__spark')),
        el('h3', { class: 'shd-wl-empty__title', text: 'This link doesn’t have any items' }),
        el('p', { class: 'shd-wl-empty__text', text: 'Ask your friend to share their list again, or have a look around.' }),
        el('a', { class: 'shd-wl-pill shd-wl-pill--teal', href: rootEl.dataset.shopUrl || root + 'collections/all' }, rootEl.dataset.shopLabel || 'Start browsing')));
      return;
    }
    body.append(el('div', { class: 'shd-wl-toolbar' },
      el('span', { class: 'shd-wl-toolbar__count', text: SHARED.items.length === 1 ? '1 item' : SHARED.items.length + ' items' }),
      el('div', { class: 'shd-wl-toolbar__actions' },
        loggedIn ? el('a', { class: 'shd-wl-pill shd-wl-pill--ghost shd-wl-pill--sm', href: cfg.pageUrl }, 'My wish list') : null)));
    const grid = el('ul', { class: 'shd-wl-grid', role: 'list' });
    SHARED.items.forEach((it) => grid.append(sharedCard(it)));
    body.append(grid);
    if (loggedIn && !DEMO) api({ action: 'get' }).then((j) => j.lists && setLists(j.lists)).catch(() => { /* keep Liquid data */ });
  }

  function initPage(rootEl) {
    if (SHARED) { initSharedPage(rootEl); return; }
    const body = rootEl.querySelector('[data-shd-wl-body]') || rootEl;
    const opts = rootEl.dataset;
    let active = safeGet(sessionStorage, 'shd_wl_tab');

    function render() {
      body.replaceChildren();
      if (!loggedIn) {
        body.append(el('div', { class: 'shd-wl-empty shd-wl-empty--login' },
          el('div', { class: 'shd-wl-empty__art', 'aria-hidden': 'true' }, icon('heart', 'shd-wl-empty__heart'), icon('sparkle', 'shd-wl-empty__spark')),
          el('h2', { class: 'shd-wl-empty__title', text: 'Log in to see your wish lists ♡' }),
          el('p', { class: 'shd-wl-empty__text', text: 'Your saved favorites live in your account, so they’re always here when you come back.' }),
          el('a', { class: 'shd-wl-pill shd-wl-pill--coral', href: loginHref() }, 'Log in')));
        return;
      }
      const lists = state.data.lists;
      if (!lists.some((l) => l.id === active)) active = lists[0].id;
      const list = lists.find((l) => l.id === active);

      const tabs = el('div', { class: 'shd-wl-tabs', role: 'tablist', 'aria-label': 'Your wish lists' });
      lists.forEach((l) => tabs.append(el('button', {
        type: 'button', role: 'tab', class: 'shd-wl-tab' + (l.id === active ? ' is-active' : ''), 'aria-selected': l.id === active ? 'true' : 'false',
        onclick: () => { active = l.id; safeSet(sessionStorage, 'shd_wl_tab', active); render(); },
      }, el('span', { class: 'shd-wl-tab__name', text: l.name }), el('span', { class: 'shd-wl-tab__count', text: String(l.items.length) }))));
      tabs.append(el('button', { type: 'button', class: 'shd-wl-tab shd-wl-tab--new', onclick: () => promptDialog({
        title: 'Start a new list', label: 'List name, e.g. Christmas ideas', submit: 'Create list',
        onSubmit: async (n) => { const r = await run({ action: 'create_list', name: n }); active = r.list_id; safeSet(sessionStorage, 'shd_wl_tab', active); render(); },
      }) }, icon('plus'), 'New list'));

      const toolbar = el('div', { class: 'shd-wl-toolbar' },
        el('h2', { class: 'shd-wl-toolbar__title', text: list.name }),
        el('span', { class: 'shd-wl-toolbar__count', text: list.items.length === 1 ? '1 item' : list.items.length + ' items' }),
        el('div', { class: 'shd-wl-toolbar__actions' },
          list.items.length ? el('button', { type: 'button', class: 'shd-wl-pill shd-wl-pill--ghost shd-wl-pill--sm shd-wl-share', onclick: () => shareList(list) }, icon('share'), 'Share list') : null,
          el('button', { type: 'button', class: 'shd-wl-iconbtn', 'aria-label': 'Rename list', title: 'Rename list', onclick: () => promptDialog({
            title: 'Rename list', label: 'List name', value: list.name, submit: 'Save', onSubmit: (n) => run({ action: 'rename_list', list_id: list.id, name: n }),
          }) }, icon('pencil')),
          lists.length > 1 ? el('button', { type: 'button', class: 'shd-wl-iconbtn', 'aria-label': 'Delete list', title: 'Delete list', onclick: () => confirmDialog({
            title: 'Delete “' + list.name + '”?', text: list.items.length ? 'The ' + list.items.length + ' saved item(s) in this list will be removed too.' : 'This list is empty.',
            confirm: 'Delete list', onConfirm: () => run({ action: 'delete_list', list_id: list.id }),
          }) }, icon('trash')) : null));

      body.append(tabs, toolbar);

      if (!list.items.length) {
        body.append(el('div', { class: 'shd-wl-empty' },
          el('div', { class: 'shd-wl-empty__art', 'aria-hidden': 'true' }, icon('heart', 'shd-wl-empty__heart'), icon('sparkle', 'shd-wl-empty__spark')),
          el('h3', { class: 'shd-wl-empty__title', text: opts.emptyHeading || 'Nothing saved here yet' }),
          el('p', { class: 'shd-wl-empty__text', text: opts.emptyText || 'Tap the ♡ on any product to save it for later.' }),
          el('a', { class: 'shd-wl-pill shd-wl-pill--teal', href: opts.shopUrl || root + 'collections/all' }, opts.shopLabel || 'Start browsing')));
        return;
      }

      const grid = el('ul', { class: 'shd-wl-grid', role: 'list' });
      list.items.forEach((it) => grid.append(card(it, list, lists)));
      body.append(grid);
    }

    function card(it, list, lists) {
      const li = el('li', { class: 'shd-wl-card is-loading' });
      const media = el('a', { class: 'shd-wl-card__media', href: root + 'products/' + encodeURIComponent(it.handle), tabindex: '-1', 'aria-hidden': 'true' });
      const title = el('a', { class: 'shd-wl-card__title', href: root + 'products/' + encodeURIComponent(it.handle) + (it.variant_id ? '?variant=' + it.variant_id : ''), text: it.handle.replace(/-/g, ' ') });
      const variant = el('p', { class: 'shd-wl-card__variant' });
      const price = el('p', { class: 'shd-wl-card__price' });
      const atc = el('button', { type: 'button', class: 'shd-wl-pill shd-wl-pill--teal shd-wl-pill--sm shd-wl-card__atc', disabled: true }, icon('cart'), el('span', { class: 'shd-wl-pill__text', text: 'Add to cart' }));
      const remove = el('button', { type: 'button', class: 'shd-wl-card__remove', 'aria-label': 'Remove from list', title: 'Remove', onclick: async () => {
        li.classList.add('is-leaving');
        try { await run({ action: 'remove', product_id: it.product_id, variant_id: it.variant_id, list_id: list.id }); toast({ title: 'Removed from ' + list.name, icon: 'heart' }); }
        catch (e) { li.classList.remove('is-leaving'); handleError(e); }
      } }, icon('heart'));
      let move = null;
      if (lists.length > 1) {
        const sel = el('select', { class: 'shd-wl-select', 'aria-label': 'Move to another list', onchange: async () => {
          const to = sel.value; if (!to) return;
          try { const r = await run({ action: 'move', product_id: it.product_id, variant_id: it.variant_id, from_list_id: list.id, to_list_id: to }); toast({ title: 'Moved to ♡ ' + r.list_name }); }
          catch (e) { sel.value = ''; handleError(e); }
        } }, el('option', { value: '', text: 'Move to…' }), lists.filter((l) => l.id !== list.id).map((l) => el('option', { value: l.id, text: l.name })));
        move = sel;
      }
      li.append(remove, media, el('div', { class: 'shd-wl-card__info' }, title, variant, price), el('div', { class: 'shd-wl-card__actions' }, atc, move));

      fetchProduct(it.handle).then((p) => {
        li.classList.remove('is-loading');
        if (!p) {
          li.classList.add('is-gone');
          variant.textContent = 'This item is no longer available.';
          atc.querySelector('.shd-wl-pill__text').textContent = 'Unavailable';
          return;
        }
        const v = (p.variants || []).find((x) => x.id === it.variant_id) || (p.variants || []).find((x) => x.available) || (p.variants || [])[0];
        title.textContent = p.title;
        if (v && p.variants.length > 1 && v.title && v.title !== 'Default Title') variant.textContent = v.title;
        const img = (v && v.featured_image && v.featured_image.src) || p.featured_image;
        if (img) media.append(el('img', { src: sizedImage(img, 600), srcset: sizedImage(img, 300) + ' 300w, ' + sizedImage(img, 600) + ' 600w', sizes: '(min-width: 990px) 25vw, 50vw', alt: p.title, loading: 'lazy', width: '600', height: '600' }));
        const cents = v ? v.price : p.price;
        price.replaceChildren(el('span', { text: money(cents) }));
        const compare = v ? v.compare_at_price : p.compare_at_price;
        if (compare && compare > cents) { price.append(el('s', { class: 'shd-wl-card__compare', text: money(compare) })); li.classList.add('is-sale'); }
        if (v && v.available) { atc.disabled = false; atc.addEventListener('click', () => addToCart(v.id, atc)); }
        else { atc.querySelector('.shd-wl-pill__text').textContent = 'Sold out'; li.classList.add('is-soldout'); }
      });
      return li;
    }

    render();
    document.addEventListener('shd-wishlist:change', render);
    if (loggedIn && !DEMO) api({ action: 'get' }).then((j) => j.lists && setLists(j.lists)).catch(() => { /* proxy not live: keep Liquid data */ });
  }

  function init() {
    syncButtons();
    document.querySelectorAll('[data-shd-wishlist-page]').forEach(initPage);
    if (DEMO) document.documentElement.classList.add('shd-wl-demo');
    resumePending();
  }

  window.SHDWishlist = { state, run, isSaved, demo: DEMO, _applyAction: applyAction, showLogin, toast };
  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', init); else init();
  // Horizon re-renders product info on variant change / section reloads
  let syncQueued = false;
  const hasBtn = (n) => n.nodeType === 1 && (n.matches('.shd-wl-btn') || n.querySelector('.shd-wl-btn'));
  new MutationObserver((muts) => {
    if (syncQueued || !muts.some((m) => Array.prototype.some.call(m.addedNodes, hasBtn))) return;
    syncQueued = true;
    requestAnimationFrame(() => { syncQueued = false; syncButtons(); });
  }).observe(document.documentElement, { childList: true, subtree: true });
})();
